// PHASE_4b.6 — typed fetch wrappers for the notification INBOX (the TopBar
// bell): the list of a caller's own in-app deliveries + mark-read. Sibling of
// preferences-api.ts (same feature, different surface — see that file's
// header for the PREFERENCE-SETTINGS surface). Backend contract:
// services/platform-graph/src/routes/notifications.ts (GET /v1/notifications,
// PATCH /v1/notifications/:id) — live and tested, no frontend consumer
// existed before notification-bell.tsx (2026-07-12).
//
// Explicit adapter signatures below describe each consumed wire contract.
// Generated OpenAPI paths exist; request/response metadata varies by endpoint,
// so these casts retain the local DTO boundary.
// Every fetch unwraps the
// spec-58 envelope through `@/api/errors`.
//
// PAGINATION: KEYSET, never offset (SEM-5327). The route answers `next_cursor`
// and takes it back as `cursor`. The offset walk this replaced drifted: a
// notification arriving while earlier pages sat in cache slid every old row
// down one position, so the next window re-served a cached page's tail row and
// dropped another — callers had to dedup by delivery_id to hide it. A cursor
// pins the boundary row, so accumulating pages need no dedup and no page can
// repeat. `total` is unchanged and still the size of the whole filtered set (a
// separate `count()` over the same WHERE as the row query), so the counter
// above the list stays honest; what a cursor cannot answer is "page 7", which
// is why both surfaces append instead of numbering.
//
// UNREAD COUNT: `state=unread` narrows in SQL, BEFORE the page is cut — so a
// `limit=1` unread fetch's `total` is an exact unread count, which is what
// fetchUnreadCount() reads and the bell badge shows. Until 2026-08-06 this
// module sent no `state` at all and derived the badge from loaded pages: the
// server used to filter AFTER pagination and the client worked around it
// instead of reporting it. The server was fixed (SEM-5133) and the workaround
// outlived the bug, under a header that documented the broken contract as if
// it were the design.
//
// The list stays `state=all` by default and tells read apart from unread via
// `status !== "read"` — the unread filter is a user choice on the full-page
// inbox, not something the bell imposes.
//
// SEM-6368/6369/6370 added three more calls against the same inbox:
// GET /v1/notifications/facets (whole-inbox counts the rail + stat cards need
// and no page can produce), the `group` filter on the list route, and
// POST /v1/notifications/mark-all-read. fetchInboxWindow() is not an endpoint
// — it is a bounded client-side page walk; its header says why.
//
// IDEMPOTENCY: PATCH /v1/notifications/:id requires `x-idempotency-key`
// server-side (400 validation envelope without it — idempotency-fix-wave
// Batch 8b). markNotificationRead() carries a fresh crypto.randomUUID() per
// call via the shared idempotencyHeader() helper (preferences-api.ts,
// assets/api.ts precedent). EMPLOYEE_SELF: the server scopes the list to the
// caller's own deliveries and 404s (never 403 — don't leak existence) on
// someone else's delivery id. No money in this feature.

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";

/** A fresh idempotency key per logical mutation (idempotency-keys invariant). */
function idempotencyHeader(): Record<string, string> {
  return { "x-idempotency-key": crypto.randomUUID() };
}

/** Page size for the inbox list (keyset-paginated — see module header). */
export const INBOX_PAGE_SIZE = 20;

/**
 * Shared cache key for the unread count. The bell renders it and the inbox
 * screen invalidates it on every mark-read — one key, or the badge keeps
 * counting a notification the user just read on the other surface.
 */
export const UNREAD_COUNT_QUERY_KEY = ["notifications", "unread-count"] as const;

/** One in-app delivery row from GET /v1/notifications. */
export interface InboxNotification {
  readonly delivery_id: string;
  readonly notification_id: string;
  readonly type: string;
  readonly subject: string;
  readonly body: string;
  readonly link: string | null;
  readonly priority: string;
  readonly status: string;
  readonly queued_at: string | null;
  readonly delivered_at: string | null;
  readonly read_at: string | null;
}

/** The mark-read PATCH response — the delivery's persisted read state. */
export interface MarkReadResult {
  readonly delivery_id: string;
  readonly status: string;
  readonly read_at: string | null;
}

// --------------------------------------------------------------------------
// Fetchers use the explicit wire signatures below.
// --------------------------------------------------------------------------

type InboxGet = (
  path: "/v1/notifications",
  init: {
    params: {
      query: { limit: number; cursor?: string; state?: "unread" | "all"; group?: string };
    };
  },
) => Promise<
  EnvelopedFetchResult<{
    notifications: InboxNotification[];
    total: number;
    next_cursor: string | null;
  }>
>;

/** One keyset page, the size of the whole filtered set, and the next cursor. */
export interface InboxPage {
  readonly notifications: readonly InboxNotification[];
  readonly total: number;
  /** Pass back as `cursor` for the following page; null on the last page. */
  readonly nextCursor: string | null;
}

/**
 * Fetch one page of the caller's OWN inbox (queued_at DESC, server-pinned
 * scope — employee_self, ADR-008). Paging is keyset, not offset (SEM-5327):
 * the inbox grows at the top of the sort, so an offset window re-served rows a
 * fresh arrival had pushed down. `unreadOnly` and `group` narrow in SQL, so
 * the returned `total` is the total of THAT filtered set. Filtering either of
 * them client-side would filter one page while the counter counted the whole
 * inbox.
 */
export async function fetchInboxPage({
  cursor,
  limit = INBOX_PAGE_SIZE,
  unreadOnly = false,
  group,
}: {
  readonly cursor?: string | undefined;
  readonly limit?: number;
  readonly unreadOnly?: boolean;
  readonly group?: string | undefined;
}): Promise<InboxPage> {
  const get = platformGraph.GET as unknown as InboxGet;
  const result = await unwrapEnvelope(
    get("/v1/notifications", {
      params: {
        query: {
          limit,
          ...(cursor !== undefined && { cursor }),
          ...(unreadOnly && { state: "unread" as const }),
          ...(group !== undefined && { group }),
        },
      },
    }),
  );
  return {
    notifications: result.notifications,
    total: result.total,
    nextCursor: result.next_cursor,
  };
}

/**
 * The caller's exact unread count. Asks for the smallest legal page (the
 * route rejects limit=0) and reads only `total` — the rows are incidental.
 */
export async function fetchUnreadCount(): Promise<number> {
  const page = await fetchInboxPage({ limit: 1, unreadOnly: true });
  return page.total;
}

/** Per-type counts behind the caller's inbox, plus the three headline totals. */
export interface InboxFacets {
  readonly total: number;
  readonly unread: number;
  readonly this_week: number;
  readonly by_type: ReadonlyArray<{
    readonly type: string;
    readonly count: number;
    readonly unread: number;
  }>;
}

type FacetsGet = (
  path: "/v1/notifications/facets",
  init: Record<string, never>,
) => Promise<EnvelopedFetchResult<InboxFacets>>;

/** Shared cache key for the facet counts — the rail and the stat cards read it. */
export const FACETS_QUERY_KEY = ["notifications", "facets"] as const;

/**
 * Counts for the caller's WHOLE inbox, not a page: the group rail's badges and
 * the three stat cards. Nothing here can be derived from a page — a rail built
 * from the rows on screen would count 25 of 600 and label it a category total.
 */
export async function fetchInboxFacets(): Promise<InboxFacets> {
  const get = platformGraph.GET as unknown as FacetsGet;
  return unwrapEnvelope(get("/v1/notifications/facets", {}));
}

/** Rows the search box is allowed to look at before it stops paging. */
export const INBOX_SEARCH_ROW_CAP = 600;

/** The list route's maximum page size — the window pages at the largest step. */
export const INBOX_WINDOW_PAGE_SIZE = 200;

/** A bounded window of the caller's inbox, and whether the server had more. */
export interface InboxWindow {
  readonly rows: readonly InboxNotification[];
  readonly truncated: boolean;
}

/**
 * Page the SAME read route up to `cap` rows so the search box can filter over
 * text the reader can actually see.
 *
 * WHY A WINDOW AND NOT A SERVER `q=`: the stored `subject`/`body` are the text
 * FROZEN at delivery time, while this screen renders `copy_spec` in the
 * reader's current language at read time (migration 0203). A SQL ILIKE would
 * search the frozen wording — for a user who has since switched language, it
 * would search text that is nowhere on their screen. Bounded and honest beats
 * server-side and wrong.
 *
 * `cap` and `fetchPage` are injected rather than closed over because the cap
 * and truncation rules are this function's whole safety property, and a screen
 * test that mocks the function outright cannot see them (audit-api.ts
 * precedent).
 */
export async function fetchInboxWindow(
  {
    unreadOnly = false,
    group,
  }: { readonly unreadOnly?: boolean; readonly group?: string | undefined } = {},
  options: {
    readonly cap?: number;
    readonly fetchPage?: typeof fetchInboxPage;
  } = {},
): Promise<InboxWindow> {
  const cap = options.cap ?? INBOX_SEARCH_ROW_CAP;
  const fetchPage = options.fetchPage ?? fetchInboxPage;
  const rows: InboxNotification[] = [];
  let cursor: string | undefined;
  let total = 0;

  // The walk follows the server's cursor (SEM-5327). Under the old offset walk
  // a notification arriving mid-walk shifted every later page down by one, so
  // the window could hold the same row twice and miss another.
  for (;;) {
    const page = await fetchPage({
      ...(cursor !== undefined && { cursor }),
      limit: INBOX_WINDOW_PAGE_SIZE,
      unreadOnly,
      ...(group !== undefined && { group }),
    });
    rows.push(...page.notifications);
    total = page.total;
    if (rows.length >= cap) {
      // Report truncation only when the server actually had more. Landing
      // exactly on the cap with nothing behind it is a COMPLETE window, and
      // warning about it would teach the reader to ignore the warning.
      return { rows: rows.slice(0, cap), truncated: total > cap };
    }
    if (page.nextCursor === null) break;
    cursor = page.nextCursor;
  }

  return { rows, truncated: false };
}

/** The filters a bulk mark-read applies — the same axes the list route takes. */
export interface MarkAllReadFilters {
  readonly state?: "unread" | "all";
  readonly group?: string;
}

type MarkAllReadPost = (
  path: "/v1/notifications/mark-all-read",
  init: { body: MarkAllReadFilters; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ marked_count: number }>>;

/**
 * Mark the caller's whole FILTERED unread set read in one call.
 *
 * The body must carry the filters the screen is showing. The server builds the
 * target set from them, so sending `{}` while a group pill is active would
 * clear notifications the reader cannot see.
 *
 * A fresh key per click: each click is its own logical action. Retrying a
 * failed call with the SAME key is safe (the server replays the original
 * count); a retry with a different body under the same key is a 409.
 */
export async function markAllNotificationsRead(
  filters: MarkAllReadFilters = {},
): Promise<number> {
  const post = platformGraph.POST as unknown as MarkAllReadPost;
  const result = await unwrapEnvelope(
    post("/v1/notifications/mark-all-read", {
      body: filters,
      headers: idempotencyHeader(),
    }),
  );
  return result.marked_count;
}

type NotificationPatch = (
  path: "/v1/notifications/{id}",
  init: { params: { path: { id: string } }; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<MarkReadResult>>;

/**
 * Mark one delivery read. `deliveryId` is the row's `delivery_id` (NOT
 * `notification_id` — the PATCH path param is the delivery). No request
 * body. A replay with the same idempotency key returns the original
 * response; a fresh key per call is still correct here since each mark-read
 * is its own logical action (idempotency-keys invariant).
 */
export async function markNotificationRead(deliveryId: string): Promise<MarkReadResult> {
  const patch = platformGraph.PATCH as unknown as NotificationPatch;
  return unwrapEnvelope(
    patch("/v1/notifications/{id}", {
      params: { path: { id: deliveryId } },
      headers: idempotencyHeader(),
    }),
  );
}
